import { useClinicalRecordLock } from '../../../../../components/clinical-record-lock';
import { Box } from '@mui/material';
import LastEyeExam from './LastEyeExam';
import General from './General';
import FormProvider from '../../../../../components/hook-form/FormProvider';
import { useForm } from 'react-hook-form';
import { ICLHistoryAndSymptoms } from '../../../../../@types/clHistoryAndSymptoms.type';
import { LoadingButton } from '@mui/lab';
import { CLHistoryAndSymptomsService } from '../../../../../api/contact-lenses/cl-history-and-symptoms.service';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from '../../../../../components/snackbar';
import { getApiErrorMessage } from '../../../../../utils/util';
import { useEffect, useState } from 'react';
import { ClinicalRecords } from '../../../../../api/clinicalRecords.service';
import { IClinicalRecordSchema } from '../../../../../@types/clinicalRecord.type';
import { useAppContext } from '../../../../../providers/AppContext';

type Props = {
  clHistorySymptoms: ICLHistoryAndSymptoms;
  clinicalRecordId: number;
  showCLForm: boolean;
  tab: (type: string) => void;
  onUpdate?: () => Promise<any> | void;
  setClinicalRecordId: (id: number) => void;
  getAllCLHistoryAndSymptoms: (clHistoryId: number, clinicalId: number) => void;
  setSelectedRow: (selectedRow: string) => void;
  finalData: any;
};

const CLHistoryAndSymptoms = ({
  clHistorySymptoms,
  clinicalRecordId,
  showCLForm,
  tab,
  onUpdate,
  setClinicalRecordId,
  getAllCLHistoryAndSymptoms,
  setSelectedRow,
  finalData,
}: Props) => {
  const lock = useClinicalRecordLock();
  const [previousRecords, setPreviousRecords] = useState(
    {} as IClinicalRecordSchema,
  );

  const methods = useForm<ICLHistoryAndSymptoms>();

  const { handleSubmit } = methods;
  const {
    formState: { isSubmitting },
  } = methods;

  const { patientId } = useParams();
  const { getAllClinicalRecords } = useAppContext();

  const onSubmit = async (data: ICLHistoryAndSymptoms) => {
    const clHistoryAndSymptomsService = new CLHistoryAndSymptomsService();

    // Amending a signed record: stage the change instead of saving it.
    if (
      clinicalRecordId &&
      lock.stageIfAmending('contactLensHistoryAndSymptoms', {
        ...data,
        clinicalRecordId: Number(clinicalRecordId),
      } as Record<string, unknown>)
    ) {
      tab('anterior_eye');
      return;
    }

    try {
      const response =
        await clHistoryAndSymptomsService.createNewHistoryAndSymptoms(
          patientId!,
          {
            ...data,
            clinicalRecordId: clinicalRecordId
              ? Number(clinicalRecordId)
              : undefined,
          },
        );
      enqueueSnackbar('CL History and Symptoms Created Successfully!', {
        variant: 'success',
      });
      if (onUpdate) {
        await onUpdate();
      } else {
        getAllClinicalRecords(patientId);
      }
      getAllCLHistoryAndSymptoms(
        response.data?.id,
        response.data?.clinicalRecordId,
      );
      setClinicalRecordId(response.data?.clinicalRecordId);
      setSelectedRow(response.data?.clinicalRecordId);
      tab('anterior_eye');
    } catch (error: unknown) {
      // `error.message[0]` showed the first *character* of a string message.
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to save CL history and symptoms'),
        {
          variant: 'error',
        },
      );
    }
  };

  const getPreviousRecords = () => {
    if (!patientId) return;
    const clinicalRecordService = new ClinicalRecords();
    clinicalRecordService.getLastRecord(patientId!).then((res) => {
      setPreviousRecords(res.data);
    });
  };
  useEffect(() => {
    getPreviousRecords();
  }, [setPreviousRecords]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LastEyeExam previousRecords={previousRecords} finalData={finalData} />
        <General
          clHistorySymptoms={clHistorySymptoms}
          showCLForm={showCLForm}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            mr: 5,
            mt: 5,
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save And Next
          </LoadingButton>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default CLHistoryAndSymptoms;
